<template>
  <Tray.Root
    v-slot="{ isCompact }"
    :open="selection !== undefined"
    :wrap="wrappable"
  >
    <div
      class="mr-4 flex items-center gap-4 pl-2"
      :class="[!isCompact ? `h-8 shrink-0` : `flex-wrap`]"
    >
      <Forms.Checkbox v-model="fullSelection" :indeterminate="indeterminate">
        <span class="whitespace-nowrap">{{ label }}</span>
      </Forms.Checkbox>

      <button
        v-if="
          selection?.mode === 'some' &&
          selection.items.length === items.length &&
          totalCount !== undefined &&
          items.length < totalCount
        "
        type="button"
        class="text-blue-500 hover:underline"
        @click="selection = { mode: 'all_except', items: [] }"
      >
        Select all {{ totalCount }} {{ noun(totalCount) }}
      </button>
    </div>

    <slot name="extra-actions"></slot>

    <Tray.Actions :actions="actions" />
  </Tray.Root>
</template>

<script setup lang="ts" generic="T">
import { computed, toValue, unref } from "vue";

import Forms from "@/design_system/Form";
import Tray from "@/design_system/Tray";
import { useKeybinds } from "@/lib/hotkeys";
import type { SelectionOptions } from "@/lib/tanstack-table";
import type { Selection } from "@/types/selection";

const selection = defineModel<Selection<T>>();

const props = defineProps<{
  definition: NonNullable<SelectionOptions<T>["widget"]>;
  items: T[];
  totalCount?: number;
  wrappable?: boolean;
  onCanSelect?: (item: T, state: Selection<T> | undefined) => boolean;
}>();

const selectAllItems = (): Selection<T> | undefined | null => {
  const canSelect = props.onCanSelect;
  const items = props.items;

  if (items.length === 0) {
    return null;
  }

  if (!canSelect) {
    return {
      mode: "some",
      items: items as [T, ...T[]],
    };
  }

  let state: Selection<T> | undefined;

  for (const item of items) {
    if (!canSelect(item, state)) {
      continue;
    }

    if (!state) {
      state = {
        mode: "some",
        items: [item],
      };
    } else {
      state.items.push(item);
    }
  }

  return state;
};

const actions = computed(() => {
  const raw = unref(props.definition.actions);
  if (typeof raw === "function") {
    const current = selection.value;
    if (!current) {
      return [];
    }

    return raw(current);
  }

  return raw;
});

const noun = (count: number) => {
  const labels = toValue(props.definition.labels);
  return count === 1 ? labels.one : labels.many;
};

const label = computed((): string | null => {
  const current = selection.value;
  if (!current) {
    return null;
  }

  switch (current.mode) {
    case "some": {
      const including = current.items.length;

      return `${including} ${noun(including)} selected`;
    }
    case "all_except": {
      const total = props.totalCount ?? props.items.length;
      const excluding = current.items.length;
      const remaining = total - excluding;

      if (excluding === 0) {
        return `All ${total} ${noun(total)} selected`;
      } else {
        return `${remaining} ${noun(
          remaining
        )} selected (${excluding} excluded)`;
      }
    }
  }

  return null;
});

const fullSelection = computed({
  get(): boolean {
    const current = selection.value;
    if (!current) {
      return false;
    }

    switch (current.mode) {
      case "some": {
        return current.items.length === props.items.length;
      }
      case "all_except": {
        return current.items.length === 0;
      }
    }

    return false;
  },
  set(next) {
    const current = selection.value;

    if (!current || current.mode === "some") {
      if (next) {
        const state = selectAllItems();
        if (state === null) {
          return;
        }

        selection.value = state;
      } else {
        selection.value = undefined;
      }
    } else {
      if (next) {
        selection.value = { mode: "all_except", items: [] };
      } else {
        selection.value = undefined;
      }
    }
  },
});

const indeterminate = computed(() => {
  const current = selection.value;
  if (!current || fullSelection.value) {
    return false;
  }

  switch (current.mode) {
    case "some": {
      const size = current.items.length;
      const total = props.items.length;

      return size > 0 && size < total;
    }
    case "all_except": {
      const size = current.items.length;
      const total = props.totalCount ?? 0;

      return size > 0 && size < total;
    }
  }

  return false;
});

useKeybinds({
  Escape: {
    name: `Unselect all items`,
    group: `Selection`,
    hidden: computed(() => {
      return selection.value === undefined;
    }),
    run(ev) {
      if (ev.defaultPrevented) {
        return;
      }

      const current = selection.value;
      if (!current) {
        return;
      }

      selection.value = undefined;
      return true;
    },
  },
  "$mod+a": {
    name: `Select all items`,
    group: `Selection`,
    hidden: computed(() => {
      return fullSelection.value;
    }),
    run(ev) {
      if (ev.defaultPrevented) {
        return;
      }

      const state = selectAllItems();
      if (state === null) {
        return true;
      }

      selection.value = state;
      return true;
    },
  },
});
</script>
